import { useState } from 'react'
import { Form, Modal } from 'antd'
import dayjs from 'dayjs'
import type { InvoiceFormValues } from '@api/useSupplierReconciliation/types/invoice'
import type { SupplierReconciliationMetadataResponse } from '../../../../../../api/useMetadata/types'

interface UseInvoiceFormStateProps {
  onCancel: () => void
  onSuccess?: () => void
}

export function useInvoiceFormState({ onCancel, onSuccess }: UseInvoiceFormStateProps) {
  const [form] = Form.useForm<InvoiceFormValues>()
  const [hasChanges, setHasChanges] = useState(false)

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Confirm Cancel',
        content: 'Are you sure you want to cancel? All entered data will be lost.',
        onOk: () => {
          onCancel()
          form.resetFields()
          setHasChanges(false)
        },
      })
    } else {
      onCancel()
      form.resetFields()
      setHasChanges(false)
    }
  }

  const handleClose = () => {
    onCancel()
  }

  const handleFormValuesChange = () => {
    setHasChanges(true)
  }

  const resetForm = () => {
    form.resetFields()
    setHasChanges(false)
  }

  const transformMetadataToDropdownOptions = (metadata?: SupplierReconciliationMetadataResponse) => {
    if (!metadata) {
      return {
        suppliers: [],
        terminals: [],
        products: [],
        states: [],
      }
    }

    const suppliers =
      metadata.suppliers?.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
      })) || []

    const terminals =
      metadata.terminals?.map((terminal) => ({
        value: terminal.id,
        label: terminal.name,
        timezone: terminal.timezone,
      })) || []

    const products = Object.values(metadata.products || {})
      .flat()
      .map((product: { id: string; name: string; code?: string }) => ({
        value: product.id,
        label: product.code ? `${product.name} (${product.code})` : product.name,
      }))

    const states =
      metadata.states?.map((state) => ({
        value: state.label,
        label: state.value,
      })) || []

    return {
      suppliers,
      terminals,
      products,
      states,
    }
  }

  const getInitialValues = () => ({
    invoiceDate: dayjs(),
    invoiceDue: undefined,
    terminal: undefined,
    billingBy: 'net',
    products: [{}],
    taxes: [],
    multipleSites: false,
    productTotal: undefined,
    taxTotal: undefined,
    grandTotal: undefined,
  })

  const handleSuccessfulSubmission = () => {
    onSuccess?.()
    resetForm()
    onCancel()
  }

  return {
    form,
    hasChanges,
    handleCancel,
    handleClose,
    handleFormValuesChange,
    resetForm,
    transformMetadataToDropdownOptions,
    getInitialValues,
    handleSuccessfulSubmission,
  }
}
